/* =========================================================
   Reportes (solo Admin ETC)
   Extracción de datos por categoría: Organizaciones · Sites ·
   Instructores · Consolidado. Filtros avanzados (tiempo /
   categoría / específicos), selector de columnas completo,
   previsualización en tabla y exportación a Excel (.xlsx) ·
   CSV · PDF (impresión).
   Dos variantes de layout (A: estudio · B: barra superior).
   ========================================================= */
import { useEffect, useMemo, useState } from 'react';
import type { SortState } from '@/types';
import { ic, sized } from '@/components/Icons';
import { DataTable, EmptyState, PageHeader, Pagination, VariantSwitch, type Column } from '@/components/ui';
import { COUNTRIES, ds } from '@/data';
import { useI18n } from '@/i18n';
import { addToast, useAppStore } from '@/store/useAppStore';
import { download, downloadCsv } from '@/lib/xlsx';
import {
  CAT_META, COLS, L, applyFilters, countryName, defaultCols, defaultFilters,
  type Category, type ColSelection, type Filters, type ReportRow,
} from './model';
import {
  ActiveFilters, CELLS, CategoryPicker, ColumnPicker, Ctl, ExportBar, FLD_LABEL, INPUT,
  SpecificFilters, TimeFilters, type Bounds, type Chip, type CountryOpt, type Exporters, type Patch,
} from './controls';
import { buildExport, openPrint } from './exportReport';

/** Numeric-looking columns sort numerically; the check samples the column
 *  rather than each pair, so an odd blank can't flip the whole order. */
function sortReport(rows: ReportRow[], sort: SortState | null, get: (r: ReportRow) => string | number | undefined) {
  if (!sort) return rows;
  const sample = rows.slice(0, 40).map(get).filter((v) => v !== '' && v != null);
  const numeric =
    sample.length > 0 && sample.every((v) => /^[\d.\s%]+$/.test(String(v)) && !isNaN(parseFloat(String(v))));
  const dir = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = get(a);
    const vb = get(b);
    const c = numeric
      ? (parseFloat(String(va)) || 0) - (parseFloat(String(vb)) || 0)
      : String(va ?? '').localeCompare(String(vb ?? ''), 'es', { numeric: true, sensitivity: 'base' });
    return c * dir;
  });
}

export function ReportsScreen() {
  const { lang } = useI18n();
  const variant = useAppStore((s) => s.variant.reports);
  const setVariantFor = useAppStore((s) => s.setVariantFor);

  const [category, setCategory] = useState<Category>('orgs');
  const [f, setF] = useState<Filters>(defaultFilters);
  const [cols, setCols] = useState<ColSelection>(defaultCols);
  const [page, setPage] = useState(1);
  const [per, setPer] = useState(25);
  const [sort, setSort] = useState<SortState | null>(null);

  const upd: Patch = (patch) => setF((s) => ({ ...s, ...patch }));
  const onPer = (n: number) => { setPer(n); setPage(1); };
  const onSort = (key: string) =>
    setSort((s) => (s && s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  function changeCategory(c: Category) {
    setCategory(c);
    setF(defaultFilters());
    setSort(null);
    setPage(1);
  }

  const fKey = category + '|' + JSON.stringify(f);
  useEffect(() => { setPage(1); }, [fKey]);

  /* option lists derived from the full category dataset */
  const fullList = useMemo<Record<string, unknown>[]>(() => {
    const list =
      category === 'sites' ? ds.listSites()
      : category === 'instructors' ? ds.listInstructors()
      : ds.listOrgs();
    return list as unknown as Record<string, unknown>[];
  }, [category]);

  const countryOpts = useMemo<CountryOpt[]>(() => {
    const key = category === 'sites' ? 'SiteCountry' : category === 'instructors' ? 'Country' : 'OrgCountry';
    const codes = Array.from(new Set(fullList.map((r) => r[key]).filter(Boolean) as string[]));
    return codes
      .map((code) => COUNTRIES.find((c) => c.code === code) || { code, name_es: code, name_en: code })
      .sort((a, b) => (lang === 'es' ? a.name_es : a.name_en).localeCompare(lang === 'es' ? b.name_es : b.name_en, 'es'));
  }, [fullList, category, lang]);

  const certOpts = useMemo(
    () => (category === 'instructors'
      ? Array.from(new Set(ds.listInstructors().map((i) => i.Certification).filter(Boolean) as string[])).sort()
      : []),
    [category],
  );

  const bounds = useMemo<Bounds>(() => {
    const years = fullList.map((r) => Number(r.YearJoined)).filter(Boolean);
    return { min: years.length ? Math.min(...years) : 2009, max: years.length ? Math.max(...years) : 2026 };
  }, [fullList]);

  /* filtered + sorted */
  const filtered = useMemo(() => applyFilters(category, f), [fKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const sorted = useMemo(() => {
    const def = sort ? COLS[category].find((c) => c.key === sort.key) : null;
    if (!def) return filtered;
    return sortReport(filtered, sort, (r) => def.get(r, lang));
  }, [filtered, sort, category, lang]);

  const total = sorted.length;
  const pages = Math.max(1, Math.ceil(total / per));
  const pageRows = sorted.slice((page - 1) * per, page * per);

  /* table columns */
  const selDefs = COLS[category].filter((c) => cols[category].includes(c.key));
  const tableCols: Column<ReportRow>[] = selDefs.map((c) => {
    const custom = CELLS[category]?.[c.key];
    return {
      key: c.key,
      label: L(lang, c.es, c.en),
      align: c.align,
      mono: c.mono,
      sortable: true,
      render: custom
        ? (r) => custom(r, lang)
        : (r) => {
            const v = c.get(r, lang);
            return v === '' || v == null ? <span style={{ color: 'var(--text-3)' }}>—</span> : v;
          },
    };
  });

  /* active filter chips */
  const chips: Chip[] = [];
  if (f.country) chips.push({ label: countryName(f.country, lang), clear: () => upd({ country: '' }) });
  if (f.yearMin || f.yearMax) {
    chips.push({ label: (f.yearMin || bounds.min) + '–' + (f.yearMax || bounds.max), clear: () => upd({ yearMin: '', yearMax: '' }) });
  }
  if (f.updatedFrom || f.updatedTo) {
    chips.push({
      label: L(lang, 'Actualizado', 'Updated') + ' ' + (f.updatedFrom || '…') + '→' + (f.updatedTo || '…'),
      clear: () => upd({ updatedFrom: '', updatedTo: '' }),
    });
  }
  if (f.completeness !== 'all') {
    chips.push({
      label: f.completeness === 'complete' ? L(lang, 'Completos', 'Complete') : L(lang, 'Pendientes', 'Pending'),
      clear: () => upd({ completeness: 'all' }),
    });
  }
  if (category === 'orgs' && f.multiSite !== 'all') {
    chips.push({ label: f.multiSite === 'multi' ? 'Multi-site' : L(lang, 'Un site', 'Single'), clear: () => upd({ multiSite: 'all' }) });
  }
  if (category === 'sites' && f.hasManager !== 'all') {
    chips.push({ label: 'Manager: ' + (f.hasManager === 'yes' ? L(lang, 'Sí', 'Yes') : 'No'), clear: () => upd({ hasManager: 'all' }) });
  }
  if (category === 'sites' && f.primary !== 'all') {
    chips.push({ label: L(lang, 'Primario: ', 'Primary: ') + (f.primary === 'yes' ? L(lang, 'Sí', 'Yes') : 'No'), clear: () => upd({ primary: 'all' }) });
  }
  if (category === 'instructors' && f.status !== 'all') {
    chips.push({
      label: f.status === 'active' ? L(lang, 'Activos', 'Active') : L(lang, 'Desasociados', 'Disassociated'),
      clear: () => upd({ status: 'all' }),
    });
  }
  if (category === 'instructors' && f.certification) {
    chips.push({ label: f.certification, clear: () => upd({ certification: '' }) });
  }
  if (category === 'consolidated' && f.status !== 'all') {
    chips.push({
      label: f.status === 'with' ? L(lang, 'Con instructores', 'With instructors') : L(lang, 'Sin instructores', 'Without instructors'),
      clear: () => upd({ status: 'all' }),
    });
  }

  /* exports */
  const stamp = new Date().toISOString().slice(0, 10);
  const fileBase = () => 'reporte_' + category + '_' + stamp;
  const reportTitle = () =>
    L(lang, 'Reporte de ', 'Report — ') + L(lang, CAT_META[category].es, CAT_META[category].en) + ' · ETC Connect · ' + stamp;

  function doXlsx() {
    const { header, data } = buildExport(category, sorted, cols[category], lang);
    download(fileBase(), [header, ...data], CAT_META[category].sheet, { title: reportTitle() });
    addToast({
      kind: 'template',
      title: L(lang, 'Excel generado', 'Excel generated'),
      body: fileBase() + '.xlsx · ' + total + ' ' + L(lang, 'registros', 'records'),
    });
  }

  function doCsv() {
    const { header, data } = buildExport(category, sorted, cols[category], lang);
    downloadCsv(fileBase(), [header, ...data], reportTitle());
    addToast({
      kind: 'template',
      title: L(lang, 'CSV generado', 'CSV generated'),
      body: fileBase() + '.csv · ' + total + ' ' + L(lang, 'registros', 'records'),
    });
  }

  function doPdf() {
    const { header, data } = buildExport(category, sorted, cols[category], lang);
    const title = L(lang, 'Reporte de ', 'Report — ') + L(lang, CAT_META[category].es, CAT_META[category].en);
    const subtitle =
      total + ' ' + L(lang, 'registros', 'records') +
      (chips.length ? ' · ' + L(lang, 'filtrado', 'filtered') : '') +
      ' · ' + selDefs.length + ' ' + L(lang, 'columnas', 'columns');
    if (openPrint(title, subtitle, header, data, lang)) {
      addToast({
        kind: 'ok',
        title: L(lang, 'Vista de impresión lista', 'Print view ready'),
        body: L(lang, 'Usa Imprimir → Guardar como PDF', 'Use Print → Save as PDF'),
      });
    } else {
      addToast({
        kind: 'err',
        title: L(lang, 'Bloqueo de ventana', 'Popup blocked'),
        body: L(lang, 'Permite ventanas emergentes para generar el PDF.', 'Allow popups to generate the PDF.'),
      });
    }
  }

  const exporters: Exporters = { onXlsx: doXlsx, onCsv: doCsv, onPdf: doPdf, disabled: total === 0 };

  const tableBlock =
    total === 0 ? (
      <EmptyState
        icon={sized(ic.search, 30)}
        title={L(lang, 'Sin resultados', 'No results')}
        body={L(
          lang,
          'Ningún registro coincide con los filtros seleccionados. Ajusta o limpia los filtros.',
          'No record matches the selected filters. Adjust or clear the filters.',
        )}
        action={<button className="btn" onClick={() => setF(defaultFilters())}>{L(lang, 'Limpiar filtros', 'Clear filters')}</button>}
      />
    ) : (
      <>
        <DataTable columns={tableCols} rows={pageRows} sort={sort} onSort={onSort} stickyHeader maxHeight="58vh" />
        <Pagination page={page} pageCount={pages} onPage={setPage} total={total} per={per} onPer={onPer} />
      </>
    );

  const resultsHead = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <span style={{ fontWeight: 800, fontSize: 15, color: 'var(--brand-ink)' }}>
        <span className="mono">{total}</span> {L(lang, 'resultados', 'results')}
      </span>
      <span style={{ fontSize: 12, color: 'var(--text-3)' }}>· {selDefs.length} {L(lang, 'columnas', 'columns')}</span>
      <div style={{ flex: 1 }} />
      <ActiveFilters chips={chips} lang={lang} onClearAll={() => setF(defaultFilters())} />
    </div>
  );

  const variantSwitch = (
    <VariantSwitch
      value={variant}
      onChange={(v) => setVariantFor('reports', v)}
      options={[{ value: 'A', es: 'Estudio', en: 'Studio' }, { value: 'B', es: 'Barra', en: 'Bar' }]}
    />
  );

  const title = L(lang, 'Reportes', 'Reports');
  const sub = L(
    lang,
    'Extrae y exporta datos por categoría con filtros avanzados',
    'Extract and export data by category with advanced filters',
  );

  /* ===== Variant B — top toolbar ===== */
  if (variant === 'B') {
    return (
      <div className="screen-enter" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <PageHeader title={title} sub={sub} actions={variantSwitch} />
        <CategoryPicker value={category} onChange={changeCategory} lang={lang} big />
        <ToolbarB
          f={f} upd={upd} lang={lang} bounds={bounds} countryOpts={countryOpts} certOpts={certOpts}
          category={category} cols={cols} setCols={setCols} exporters={exporters}
        />
        <div className="card" style={{ padding: '12px 16px' }}>{resultsHead}</div>
        {tableBlock}
      </div>
    );
  }

  /* ===== Variant A — studio (left filter rail) ===== */
  return (
    <div className="screen-enter" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <PageHeader
        title={title}
        sub={sub}
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {variantSwitch}
            <ColumnPicker category={category} cols={cols} setCols={setCols} lang={lang} />
            <ExportBar {...exporters} />
          </div>
        }
      />
      <div className="m-grid1" style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 320px) 1fr', gap: 14, alignItems: 'start' }}>
        <aside className="m-unstick" style={{ position: 'sticky', top: 70, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="card" style={{ padding: 12 }}>
            <div style={{ ...FLD_LABEL, marginBottom: 8 }}>{L(lang, 'Categoría', 'Category')}</div>
            <CategoryPicker value={category} onChange={changeCategory} lang={lang} />
          </div>
          <div className="card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: 'var(--brand-primary)', display: 'inline-flex' }}>{ic.sliders}</span>
              <span style={{ fontWeight: 800, fontSize: 13, color: 'var(--text)' }}>{L(lang, 'Filtros avanzados', 'Advanced filters')}</span>
            </div>
            <Ctl label={L(lang, 'Búsqueda', 'Search')} icon={ic.search}>
              <input
                value={f.q}
                onChange={(e) => upd({ q: e.target.value })}
                placeholder={L(lang, 'Nombre, ID, email…', 'Name, ID, email…')}
                style={INPUT}
              />
            </Ctl>
            <div style={{ height: 1, background: 'var(--line)' }} />
            <div style={{ ...FLD_LABEL, color: 'var(--text-3)' }}>{L(lang, 'Tiempo', 'Time')}</div>
            <TimeFilters f={f} upd={upd} lang={lang} bounds={bounds} />
            <div style={{ height: 1, background: 'var(--line)' }} />
            <div style={{ ...FLD_LABEL, color: 'var(--text-3)' }}>{L(lang, 'Específicos', 'Specific')}</div>
            <SpecificFilters category={category} f={f} upd={upd} lang={lang} countryOpts={countryOpts} certOpts={certOpts} cols={1} />
            <button className="btn ghost" onClick={() => setF(defaultFilters())} style={{ justifyContent: 'center', color: 'var(--brand-primary-d)' }}>
              {sized(ic.refresh, 14)} {L(lang, 'Limpiar filtros', 'Clear filters')}
            </button>
          </div>
        </aside>
        <section style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          <div className="card" style={{ padding: '12px 16px' }}>{resultsHead}</div>
          {tableBlock}
        </section>
      </div>
    </div>
  );
}

/* Toolbar for variant B — search + columns + export + collapsible advanced */
function ToolbarB({
  f, upd, lang, bounds, countryOpts, certOpts, category, cols, setCols, exporters,
}: {
  f: Filters; upd: Patch; lang: 'es' | 'en'; bounds: Bounds;
  countryOpts: CountryOpt[]; certOpts: string[]; category: Category;
  cols: ColSelection; setCols: React.Dispatch<React.SetStateAction<ColSelection>>;
  exporters: Exporters;
}) {
  const [openAdv, setOpenAdv] = useState(true);
  return (
    <div className="card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }}>{ic.search}</span>
          <input
            value={f.q}
            onChange={(e) => upd({ q: e.target.value })}
            placeholder={L(lang, 'Buscar por nombre, ID, email…', 'Search by name, ID, email…')}
            aria-label={L(lang, 'Buscar', 'Search')}
            style={{ ...INPUT, padding: '9px 12px 9px 34px' }}
          />
        </div>
        <button className="btn" onClick={() => setOpenAdv((o) => !o)} aria-expanded={openAdv}>
          {ic.sliders} {L(lang, 'Filtros avanzados', 'Advanced filters')}
          <span style={{ display: 'inline-flex', transition: 'transform .2s', transform: openAdv ? 'none' : 'rotate(-90deg)' }}>
            {sized(ic.chevD, 14)}
          </span>
        </button>
        <ColumnPicker category={category} cols={cols} setCols={setCols} lang={lang} />
        <div style={{ width: 1, height: 26, background: 'var(--line)' }} />
        <ExportBar {...exporters} />
      </div>
      {openAdv ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
          <div>
            <div style={{ ...FLD_LABEL, color: 'var(--text-3)', marginBottom: 8 }}>{L(lang, 'Tiempo', 'Time')}</div>
            <TimeFilters f={f} upd={upd} lang={lang} bounds={bounds} twoCol />
          </div>
          <div>
            <div style={{ ...FLD_LABEL, color: 'var(--text-3)', marginBottom: 8 }}>{L(lang, 'Específicos', 'Specific')}</div>
            <SpecificFilters
              category={category} f={f} upd={upd} lang={lang}
              countryOpts={countryOpts} certOpts={certOpts} cols={category === 'orgs' ? 3 : 4}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
